import React, { useState, useEffect, useRef, memo } from 'react';
import styled, { keyframes } from 'styled-components';
import CopyTimeModal from './CopyTimeModal';

// Animações
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const scanline = keyframes`
  0% { transform: translateY(0); }
  100% { transform: translateY(100%); }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(0, 200, 255, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(0, 200, 255, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 200, 255, 0); }
`;

const iconPulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

const glowEffect = keyframes`
  0% { filter: drop-shadow(0 0 2px rgba(100, 182, 255, 0.3)); }
  50% { filter: drop-shadow(0 0 5px rgba(100, 182, 255, 0.7)); }
  100% { filter: drop-shadow(0 0 2px rgba(100, 182, 255, 0.3)); }
`;

// Estilos
const Container = styled.div`
  position: relative;
  background: rgba(7, 15, 29, 0.8);
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 0 20px rgba(0, 150, 255, 0.2);
  border: 1px solid rgba(0, 150, 255, 0.3);
  animation: ${fadeIn} 0.5s ease-out;
  overflow: hidden;
  max-width: 800px;
  margin: 0 auto 20px;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, rgba(0, 100, 255, 0), rgba(0, 200, 255, 0.8), rgba(0, 100, 255, 0));
    z-index: 1;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(transparent 50%, rgba(0, 30, 60, 0.1) 50%);
    background-size: 100% 4px;
    pointer-events: none;
    animation: ${scanline} 8s linear infinite;
    opacity: 0.3;
  }
`;

const Title = styled.h3`
  color: #fff;
  font-size: 1.2rem;
  margin-bottom: 15px;
  letter-spacing: 0.5px;
  text-shadow: 0 0 10px rgba(0, 150, 255, 0.5);
  text-align: center;
  
  &::after {
    content: '';
    display: block;
    width: 100%;
    height: 1px;
    background: linear-gradient(90deg, rgba(0, 100, 255, 0), rgba(0, 200, 255, 0.5), rgba(0, 100, 255, 0));
    margin-top: 10px;
  }
`;

const DayContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 10px;
  padding: 10px 15px;
  background: rgba(0, 20, 40, 0.3);
  border-radius: 8px;
  border: 1px solid rgba(0, 150, 255, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 30, 60, 0.4);
    border-color: rgba(0, 150, 255, 0.4);
  }
`;

const DayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
`;

const DayName = styled.span`
  color: #fff;
  font-weight: 500;
  letter-spacing: 0.5px;
  min-width: 120px;
  flex: 0 0 auto;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 0 15px;
  flex: 0 0 auto;
`;

const ToggleLabel = styled.span`
  color: ${props => props.isActive ? '#64ffda' : '#8a94a7'};
  margin-right: 8px;
  font-size: 0.85rem;
  transition: color 0.3s ease;
  white-space: nowrap;
  display: none; /* Ocultar o texto */
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 40px;
  height: 20px;
  cursor: pointer;
  flex-shrink: 0;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  
  &:checked + span {
    background-color: rgba(0, 200, 255, 0.6);
  }
  
  &:checked + span:before {
    transform: translateX(20px);
    background-color: #fff;
    box-shadow: 0 0 8px rgba(0, 200, 255, 0.8);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(100, 100, 100, 0.4);
  transition: 0.4s;
  border-radius: 20px;
  
  &:before {
    position: absolute;
    content: "";
    height: 16px;
    width: 16px;
    left: 2px;
    bottom: 2px;
    background-color: #ccc;
    transition: 0.4s;
    border-radius: 50%;
  }
`;

const TimeInputsContainer = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  opacity: ${props => props.isActive ? 1 : 0.5};
  pointer-events: ${props => props.isActive ? 'auto' : 'none'};
  transition: opacity 0.3s ease;
  flex: 1 1 auto;
  justify-content: center;
`;

const TimeInputGroup = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 1 120px;
  min-width: 80px;
`;

const TimeLabel = styled.label`
  color: #8a94a7;
  font-size: 0.75rem;
  margin-bottom: 4px;
  text-align: center;
`;

const TimeInput = styled.input`
  background: rgba(0, 20, 40, 0.5);
  border: 1px solid rgba(0, 150, 255, 0.3);
  border-radius: 5px;
  color: #fff;
  padding: 8px 10px;
  font-size: 0.9rem;
  outline: none;
  transition: all 0.3s ease;
  cursor: pointer;
  text-align: center;
  width: 100%;
  
  &:focus {
    border-color: rgba(0, 200, 255, 0.6);
    box-shadow: 0 0 0 2px rgba(0, 200, 255, 0.2);
  }
  
  &:hover:not(:focus) {
    border-color: rgba(0, 150, 255, 0.5);
  }
`;

const TimeSeparator = styled.div`
  color: rgba(0, 150, 255, 0.7);
  font-size: 1.2rem;
  font-weight: bold;
  margin-top: 22px;
  display: flex;
  align-items: center;
  flex: 0 0 auto;
`;

const TimePickerWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const CopyButton = styled.button`
  background: rgba(0, 150, 255, 0.1);
  color: #64B6FF;
  border: 1px solid rgba(0, 150, 255, 0.3);
  border-radius: 5px;
  padding: 6px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 10px;
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  position: relative;
  overflow: hidden;
  animation: ${iconPulse} 2s infinite ease-out;
  
  &:hover {
    background: rgba(0, 150, 255, 0.2);
    border-color: rgba(0, 150, 255, 0.5);
    box-shadow: 0 0 8px rgba(0, 150, 255, 0.4);
    transform: translateY(-1px);
    filter: drop-shadow(0 0 5px rgba(100, 182, 255, 0.7));
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, rgba(0, 150, 255, 0), rgba(100, 182, 255, 0.8), rgba(0, 150, 255, 0));
    z-index: 1;
  }
  
  svg {
    width: 18px;
    height: 18px;
    animation: ${glowEffect} 3s infinite ease-in-out;
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
`;

const TimePickerDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: rgba(7, 15, 29, 0.95);
  border: 1px solid rgba(0, 150, 255, 0.4);
  border-radius: 5px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
  z-index: 100;
  padding: 10px;
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const TimePickerControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const TimePickerSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 45%;
`;

const TimePickerLabel = styled.div`
  color: rgba(0, 150, 255, 0.8);
  font-size: 0.8rem;
  margin-bottom: 5px;
`;

const TimePickerButtons = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const TimePickerValue = styled.div`
  background: rgba(0, 30, 60, 0.5);
  color: #fff;
  font-size: 1.2rem;
  padding: 5px 0;
  text-align: center;
  border-radius: 5px;
  margin: 5px 0;
  border: 1px solid rgba(0, 150, 255, 0.3);
`;

const TimePickerButton = styled.button`
  background: rgba(0, 50, 100, 0.5);
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 100, 200, 0.5);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const TimePickerSeparator = styled.div`
  color: rgba(0, 150, 255, 0.8);
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 20px;
`;

const TimePickerActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
`;

const TimePickerActionButton = styled.button`
  background: ${props => props.primary ? 'rgba(0, 150, 255, 0.6)' : 'rgba(50, 50, 70, 0.5)'};
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 6px 12px;
  margin-left: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.primary ? 'rgba(0, 180, 255, 0.7)' : 'rgba(70, 70, 90, 0.6)'};
  }
`;

const InfoText = styled.div`
  color: #8a94a7;
  font-size: 0.85rem;
  margin-top: 15px;
  padding: 10px;
  background: rgba(0, 30, 60, 0.2);
  border-radius: 5px;
  border-left: 3px solid rgba(0, 150, 255, 0.5);
  display: none;
`;

const GridLines = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: -1;
`;

const GridLine = styled.div`
  position: absolute;
  background-color: rgba(0, 150, 255, 0.1);
  
  ${props => props.horizontal 
    ? `
      left: 0;
      right: 0;
      height: 1px;
      top: ${props.position}%;
    ` 
    : `
      top: 0;
      bottom: 0;
      width: 1px;
      left: ${props.position}%;
    `
  }
`;

/**
 * Componente para selecionar disponibilidade por dia da semana
 * @param {Object} props - Propriedades do componente
 * @param {number} props.duration - Duração do agendamento em minutos
 * @param {function} props.onChange - Função chamada quando a disponibilidade muda
 */
const AvailabilitySelector = ({ duration = 60, onChange }) => {
  // Estado para armazenar a disponibilidade de cada dia
  const [availability, setAvailability] = useState({
    'domingo': { available: false, startTime: '09:00', endTime: '18:00' },
    'segunda-feira': { available: true, startTime: '09:00', endTime: '18:00' },
    'terça-feira': { available: true, startTime: '09:00', endTime: '18:00' },
    'quarta-feira': { available: true, startTime: '09:00', endTime: '18:00' },
    'quinta-feira': { available: true, startTime: '09:00', endTime: '18:00' },
    'sexta-feira': { available: true, startTime: '09:00', endTime: '18:00' },
    'sábado': { available: false, startTime: '09:00', endTime: '18:00' }
  });
  
  // Estado para controlar o time picker
  const [openTimePicker, setOpenTimePicker] = useState(null);
  
  // Estado temporário para armazenar o horário sendo editado
  const [tempTime, setTempTime] = useState({ hours: 0, minutes: 0 });
  
  // Referência para detectar cliques fora do time picker
  const timePickerRef = useRef(null);
  
  // Referência para o conteúdo do modal
  const modalContentRef = useRef(null);
  
  // Estado para controlar o modal de copiar horários
  const [copyModal, setCopyModal] = useState({
    isOpen: false,
    sourceDay: null,
    selectedDays: {}
  });
  
  // Enviar a disponibilidade inicial formatada assim que o componente for montado
  useEffect(() => {
    if (onChange) {
      const formattedAvailability = formatAvailabilityForWebhook(availability);
      console.log('Disponibilidade inicial formatada:', formattedAvailability);
      onChange(formattedAvailability);
    }
  }, []);
  
  // Efeito para fechar o time picker ao clicar fora dele
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Verificar se o clique foi fora do time picker e não em um botão de ajuste
      if (
        timePickerRef.current && 
        !timePickerRef.current.contains(event.target) &&
        !event.target.closest('.time-picker-button') // Não fechar se clicar nos botões
      ) {
        // Ao clicar fora, aplicamos a alteração automaticamente
        if (openTimePicker) {
          applySelectedTime();
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openTimePicker, tempTime]);
  
  // Efeito para fechar o modal ao clicar fora dele
  useEffect(() => {
    const handleModalClickOutside = (event) => {
      if (
        copyModal.isOpen &&
        modalContentRef.current && 
        !modalContentRef.current.contains(event.target)
      ) {
        handleCloseCopyModal();
      }
    };
    
    document.addEventListener('mousedown', handleModalClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleModalClickOutside);
    };
  }, [copyModal.isOpen]);
  
  // Função para abrir o time picker
  const handleOpenTimePicker = (day, field) => {
    const currentTime = availability[day][field];
    const [hours, minutes] = currentTime.split(':').map(Number);
    
    setTempTime({ hours, minutes });
    setOpenTimePicker({ day, field });
  };
  
  // Função para ajustar as horas
  const adjustHours = (delta) => {
    setTempTime(prev => {
      let newHours = prev.hours + delta;
      
      // Garantir que as horas estejam entre 0 e 23
      if (newHours < 0) newHours = 23;
      if (newHours > 23) newHours = 0;
      
      // Aplicar automaticamente a alteração
      const newTime = { ...prev, hours: newHours };
      applyTimeChange(newTime);
      
      return newTime;
    });
  };
  
  // Função para ajustar os minutos
  const adjustMinutes = (delta) => {
    setTempTime(prev => {
      let newMinutes = prev.minutes + delta;
      let newHours = prev.hours;
      
      // Ajustar horas se os minutos ultrapassarem os limites
      if (newMinutes < 0) {
        newMinutes = 59;
        newHours = newHours - 1 < 0 ? 23 : newHours - 1;
      }
      if (newMinutes > 59) {
        newMinutes = 0;
        newHours = newHours + 1 > 23 ? 0 : newHours + 1;
      }
      
      // Aplicar automaticamente a alteração
      const newTime = { hours: newHours, minutes: newMinutes };
      applyTimeChange(newTime);
      
      return newTime;
    });
  };
  
  // Função para aplicar a alteração de tempo
  const applyTimeChange = (newTime) => {
    if (!openTimePicker) return;
    
    const { day, field } = openTimePicker;
    const formattedTime = `${newTime.hours.toString().padStart(2, '0')}:${newTime.minutes.toString().padStart(2, '0')}`;
    
    // Atualizar o horário no estado
    const updatedAvailability = { ...availability };
    updatedAvailability[day][field] = formattedTime;
    
    // Se o campo for startTime, verificar se o endTime precisa ser ajustado
    if (field === 'startTime') {
      const startTime = convertTimeToMinutes(formattedTime);
      const endTime = convertTimeToMinutes(updatedAvailability[day].endTime);
      
      // Verificar se o endTime é menor ou igual ao startTime + duração
      if (endTime <= startTime || endTime < startTime + duration) {
        // Calcular novo endTime (startTime + duração)
        const newEndTime = startTime + duration;
        const hours = Math.floor(newEndTime / 60);
        const minutes = newEndTime % 60;
        
        updatedAvailability[day].endTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      }
    }
    
    setAvailability(updatedAvailability);
    
    // Notificar a mudança
    if (onChange) {
      onChange(formatAvailabilityForWebhook(updatedAvailability));
    }
  };
  
  // Função para aplicar o tempo selecionado (mantida para compatibilidade)
  const applySelectedTime = () => {
    if (!openTimePicker) return;
    
    applyTimeChange(tempTime);
    setOpenTimePicker(null);
  };
  
  // Função para converter horário para minutos
  const convertTimeToMinutes = (time) => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };
  
  // Função para formatar a disponibilidade no formato exigido pelo webhook
  const formatAvailabilityForWebhook = (availability) => {
    const daysOfWeek = [
      'domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 
      'quinta-feira', 'sexta-feira', 'sábado'
    ];
    
    return daysOfWeek.map(day => {
      const dayData = availability[day];
      
      if (!dayData.available || !dayData.startTime || !dayData.endTime) {
        return `${day.charAt(0).toUpperCase() + day.slice(1)}:indisponível`;
      }
      
      // Converter horários para minutos para facilitar o cálculo
      const startMinutes = convertTimeToMinutes(dayData.startTime);
      const endMinutes = convertTimeToMinutes(dayData.endTime);
      
      // Gerar slots baseados na duração
      const slots = [];
      for (let time = startMinutes; time < endMinutes; time += duration) {
        const slotStart = `${Math.floor(time / 60).toString().padStart(2, '0')}:${(time % 60).toString().padStart(2, '0')}`;
        const slotEnd = `${Math.floor(Math.min(time + duration, endMinutes) / 60).toString().padStart(2, '0')}:${(Math.min(time + duration, endMinutes) % 60).toString().padStart(2, '0')}`;
        slots.push(`${slotStart}-${slotEnd}`);
      }
      
      // Capitalizar primeira letra do dia e juntar os slots com ponto e vírgula
      return `${day.charAt(0).toUpperCase() + day.slice(1)}:${slots.join(';')}`;
    }).join('.');
  };
  
  // Função para abrir o modal de copiar horários
  const handleOpenCopyModal = (day) => {
    // Inicializar os dias selecionados (todos desmarcados inicialmente)
    const selectedDays = {};
    Object.keys(availability).forEach(d => {
      // Não permitir selecionar o próprio dia de origem
      selectedDays[d] = d === day ? false : false;
    });
    
    setCopyModal({
      isOpen: true,
      sourceDay: day,
      selectedDays
    });
  };
  
  // Função para fechar o modal de copiar horários
  const handleCloseCopyModal = () => {
    setCopyModal({
      isOpen: false,
      sourceDay: null,
      selectedDays: {}
    });
  };
  
  // Função para alternar a seleção de um dia no modal
  const handleToggleDaySelection = (day) => {
    setCopyModal(prev => {
      const newSelectedDays = {...prev.selectedDays};
      newSelectedDays[day] = !newSelectedDays[day];
      
      return {
        ...prev,
        selectedDays: newSelectedDays
      };
    });
  };
  
  // Função para aplicar os horários copiados aos dias selecionados
  const handleApplyCopiedTimes = () => {
    if (!copyModal.sourceDay) return;
    
    const sourceTimeData = availability[copyModal.sourceDay];
    const updatedAvailability = { ...availability };
    
    // Aplicar os horários aos dias selecionados
    Object.keys(copyModal.selectedDays).forEach(day => {
      if (copyModal.selectedDays[day]) {
        updatedAvailability[day] = {
          ...updatedAvailability[day],
          startTime: sourceTimeData.startTime,
          endTime: sourceTimeData.endTime,
          available: true // Também ativa o dia
        };
      }
    });
    
    setAvailability(updatedAvailability);
    
    // Notificar a mudança
    if (onChange) {
      onChange(formatAvailabilityForWebhook(updatedAvailability));
    }
    
    // Fechar o modal
    handleCloseCopyModal();
  };
  
  return (
    <Container>
      <GridLines>
        {[1, 2, 3, 4].map(i => (
          <React.Fragment key={`h-${i}`}>
            <GridLine horizontal position={i * 25} />
            <GridLine position={i * 25} />
          </React.Fragment>
        ))}
      </GridLines>
      
      {Object.keys(availability).map(day => (
        <DayContainer key={day}>
          <DayHeader isActive={availability[day].available}>
            <DayName>{day.charAt(0).toUpperCase() + day.slice(1)}</DayName>
            
            <ToggleContainer>
              <ToggleSwitch>
                <ToggleInput 
                  type="checkbox" 
                  checked={availability[day].available}
                  onChange={() => {
                    const newAvailability = {
                      ...availability,
                      [day]: {
                        ...availability[day],
                        available: !availability[day].available
                      }
                    };
                    setAvailability(newAvailability);
                    if (onChange) {
                      onChange(formatAvailabilityForWebhook(newAvailability));
                    }
                  }}
                />
                <ToggleSlider />
              </ToggleSwitch>
            </ToggleContainer>
            
            <TimeInputsContainer isActive={availability[day].available}>
              <TimeInputGroup>
                <TimeLabel>Início</TimeLabel>
                <TimePickerWrapper>
                  <TimeInput 
                    type="text" 
                    value={availability[day].startTime}
                    onClick={() => handleOpenTimePicker(day, 'startTime')}
                    readOnly
                  />
                  <TimePickerDropdown 
                    isOpen={openTimePicker && openTimePicker.day === day && openTimePicker.field === 'startTime'}
                    className="time-picker-dropdown"
                    ref={timePickerRef}
                  >
                    <TimePickerControls>
                      <TimePickerSection>
                        <TimePickerLabel>Hora</TimePickerLabel>
                        <TimePickerButtons>
                          <TimePickerButton 
                            onClick={(e) => {
                              e.stopPropagation(); // Impedir propagação do evento
                              adjustHours(1);
                            }}
                            className="time-picker-button"
                          >
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 8L18 14H6L12 8Z" fill="currentColor" />
                            </svg>
                          </TimePickerButton>
                          <TimePickerValue>
                            {tempTime.hours.toString().padStart(2, '0')}
                          </TimePickerValue>
                          <TimePickerButton 
                            onClick={(e) => {
                              e.stopPropagation(); // Impedir propagação do evento
                              adjustHours(-1);
                            }}
                            className="time-picker-button"
                          >
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 16L18 10H6L12 16Z" fill="currentColor" />
                            </svg>
                          </TimePickerButton>
                        </TimePickerButtons>
                      </TimePickerSection>
                      
                      <TimePickerSeparator>:</TimePickerSeparator>
                      
                      <TimePickerSection>
                        <TimePickerLabel>Minuto</TimePickerLabel>
                        <TimePickerButtons>
                          <TimePickerButton 
                            onClick={(e) => {
                              e.stopPropagation(); // Impedir propagação do evento
                              adjustMinutes(1);
                            }}
                            className="time-picker-button"
                          >
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 8L18 14H6L12 8Z" fill="currentColor" />
                            </svg>
                          </TimePickerButton>
                          <TimePickerValue>
                            {tempTime.minutes.toString().padStart(2, '0')}
                          </TimePickerValue>
                          <TimePickerButton 
                            onClick={(e) => {
                              e.stopPropagation(); // Impedir propagação do evento
                              adjustMinutes(-1);
                            }}
                            className="time-picker-button"
                          >
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 16L18 10H6L12 16Z" fill="currentColor" />
                            </svg>
                          </TimePickerButton>
                        </TimePickerButtons>
                      </TimePickerSection>
                    </TimePickerControls>
                  </TimePickerDropdown>
                </TimePickerWrapper>
              </TimeInputGroup>
              
              <TimeSeparator>-</TimeSeparator>
              
              <TimeInputGroup>
                <TimeLabel>Término</TimeLabel>
                <TimePickerWrapper>
                  <TimeInput 
                    type="text" 
                    value={availability[day].endTime}
                    onClick={() => handleOpenTimePicker(day, 'endTime')}
                    readOnly
                  />
                  <TimePickerDropdown 
                    isOpen={openTimePicker && openTimePicker.day === day && openTimePicker.field === 'endTime'}
                    className="time-picker-dropdown"
                    ref={timePickerRef}
                  >
                    <TimePickerControls>
                      <TimePickerSection>
                        <TimePickerLabel>Hora</TimePickerLabel>
                        <TimePickerButtons>
                          <TimePickerButton 
                            onClick={(e) => {
                              e.stopPropagation(); // Impedir propagação do evento
                              adjustHours(1);
                            }}
                            className="time-picker-button"
                          >
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 8L18 14H6L12 8Z" fill="currentColor" />
                            </svg>
                          </TimePickerButton>
                          <TimePickerValue>
                            {tempTime.hours.toString().padStart(2, '0')}
                          </TimePickerValue>
                          <TimePickerButton 
                            onClick={(e) => {
                              e.stopPropagation(); // Impedir propagação do evento
                              adjustHours(-1);
                            }}
                            className="time-picker-button"
                          >
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 16L18 10H6L12 16Z" fill="currentColor" />
                            </svg>
                          </TimePickerButton>
                        </TimePickerButtons>
                      </TimePickerSection>
                      
                      <TimePickerSeparator>:</TimePickerSeparator>
                      
                      <TimePickerSection>
                        <TimePickerLabel>Minuto</TimePickerLabel>
                        <TimePickerButtons>
                          <TimePickerButton 
                            onClick={(e) => {
                              e.stopPropagation(); // Impedir propagação do evento
                              adjustMinutes(1);
                            }}
                            className="time-picker-button"
                          >
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 8L18 14H6L12 8Z" fill="currentColor" />
                            </svg>
                          </TimePickerButton>
                          <TimePickerValue>
                            {tempTime.minutes.toString().padStart(2, '0')}
                          </TimePickerValue>
                          <TimePickerButton 
                            onClick={(e) => {
                              e.stopPropagation(); // Impedir propagação do evento
                              adjustMinutes(-1);
                            }}
                            className="time-picker-button"
                          >
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 16L18 10H6L12 16Z" fill="currentColor" />
                            </svg>
                          </TimePickerButton>
                        </TimePickerButtons>
                      </TimePickerSection>
                    </TimePickerControls>
                  </TimePickerDropdown>
                </TimePickerWrapper>
              </TimeInputGroup>
            </TimeInputsContainer>
            
            <ActionsContainer>
              <CopyButton 
                onClick={() => handleOpenCopyModal(day)}
                title="Copiar este horário para outros dias"
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 4V16C8 16.5304 8.21071 17.0391 8.58579 17.4142C8.96086 17.7893 9.46957 18 10 18H18C18.5304 18 19.0391 17.7893 19.4142 17.4142C19.7893 17.0391 20 16.5304 20 16V7.242C20 6.97556 19.9467 6.71181 19.8433 6.46624C19.7399 6.22068 19.5885 5.99824 19.398 5.812L16.188 2.602C16.0018 2.41148 15.7793 2.26011 15.5338 2.15673C15.2882 2.05334 15.0244 2.00001 14.758 2H10C9.46957 2 8.96086 2.21071 8.58579 2.58579C8.21071 2.96086 8 3.46957 8 4Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 18V20C16 20.5304 15.7893 21.0391 15.4142 21.4142C15.0391 21.7893 14.5304 22 14 22H6C5.46957 22 4.96086 21.7893 4.58579 21.4142C4.21071 21.0391 4 20.5304 4 20V8C4 7.46957 4.21071 6.96086 4.58579 6.58579C4.96086 6.21071 5.46957 6 6 6H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </CopyButton>
            </ActionsContainer>
          </DayHeader>
        </DayContainer>
      ))}
      
      {/* Modal para copiar horários */}
      {copyModal.isOpen && (
        <CopyTimeModal 
          isOpen={copyModal.isOpen}
          sourceDay={copyModal.sourceDay}
          selectedDays={copyModal.selectedDays}
          availability={availability}
          onToggleDay={handleToggleDaySelection}
          onApply={handleApplyCopiedTimes}
          onClose={handleCloseCopyModal}
          modalRef={modalContentRef}
        />
      )}
    </Container>
  );
};

export default AvailabilitySelector;
